import { useState } from "react";
import { action, API, controller } from "api";
import { useAppContext } from "context";
import { lsGet } from "utils/localStorage";
import { ToFormData } from "utils/formData";

// Wire values of the server's EnumLi.PosScanMode (and the desktop till's ScanMode).
const WIRE = { barcode: 0, all: 1 };

/**
 * The POS scan box's mode — "all" or "barcode" — and the ONE place its rule lives:
 *
 *   branch shows the switch (outlet.showScanModeSwitch) ? the person's own choice : "all"
 *
 * The choice is kept on the signed-in user (user.posScanMode), so it follows them to any
 * browser and the desktop till. It used to sit in this browser's localStorage, which every
 * deploy cleared (App.jsx CACHE_VERSION wipe) — the POS then reopened in Barcode, which is
 * how cashiers kept landing in the mode they had not chosen.
 *
 * The branch switch comes from the register's outlet (`activeReg.outlet`), the same copy
 * useSeparateCartLines reads, so an admin's change reaches an open POS when its register is
 * next resolved (reload / reopen). A user row from a server too old to send posScanMode,
 * or a branch row without the switch, reads as today's defaults: All, switch shown.
 *
 * setMode applies at once and saves in the background. If the save fails the choice still
 * holds for this visit, and `saveFailed` says so — the cashier is never blocked on it.
 */
const usePosScanMode = () => {
  const { authUser, setAuthUser } = useAppContext();
  const [saveFailed, setSaveFailed] = useState(false);

  const canSwitch = lsGet("activeReg")?.outlet?.showScanModeSwitch !== false;
  const chosen = authUser?.posScanMode === WIRE.barcode ? "barcode" : "all";
  const mode = canSwitch ? chosen : "all";

  const setMode = (next) => {
    if (!canSwitch || next === mode || !(next in WIRE)) return;
    setAuthUser((u) => (u ? { ...u, posScanMode: WIRE[next] } : u));
    setSaveFailed(false);
    API(controller.User + action.SetMyPosScanMode).post(
      ToFormData({ mode: WIRE[next] }),
      (res) => setSaveFailed(res?.status !== 200),
    );
  };

  return { mode, setMode, canSwitch, saveFailed };
};

export default usePosScanMode;
